import {
    Button,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import DocumentInputMasked from '../../../../components/DocumentInputMasked';
import RichTextEditor from '../../../../components/RichTextEditor';
import useErrors from '../../../../hooks/useErrors';
import { useFetch } from '../../../../hooks/useFecth';
import server from '../../../../services/server';
import {
    applyContractPlaceholders,
    countPendingPlaceholders,
    countSimplePlaceholders,
    hasContractPlaceholders,
    sanitizeContractHtmlForEditor,
} from '../../../../utils/contractTextPlaceholders';
import { fetchContractBaseTemplate } from '../../../../utils/processAndSaveContractText';

/** Defaults: representante da contratada (Lucimar) + testemunhas do template. */
const DEFAULT_SIGNATORIES = {
    contractedName: 'LUCIMAR ELIAS TEIXEIRA DONAS',
    contractedEmail: 'eurobemm@gmail.com',
    contractedDocument: '168.719.361-49',
    witness1Name: 'Artur Aquino Silva',
    witness1Email: 'arturquinosilva@gmail.com',
    witness1Document: '034.105.721-55',
    witness2Name: 'Aliny Cristina Teixeira Mendonça',
    witness2Email: 'aliny.direito@gmail.com',
    witness2Document: '033.105.721-55',
};

const schema = Yup.object().shape({
    contractText: Yup.string().required('O texto do contrato é obrigatório'),
    contractedName: Yup.string().trim().required('Nome da contratada é obrigatório'),
    contractedEmail: Yup.string().trim().email('E-mail inválido').required('E-mail da contratada é obrigatório'),
    contractedDocument: Yup.string().trim().required('Documento da contratada é obrigatório'),
    witness1Name: Yup.string().trim().required('Nome da testemunha 1 é obrigatório'),
    witness1Email: Yup.string().trim().email('E-mail inválido').required('E-mail da testemunha 1 é obrigatório'),
    witness1Document: Yup.string().trim().required('Documento da testemunha 1 é obrigatório'),
    witness2Name: Yup.string().trim().required('Nome da testemunha 2 é obrigatório'),
    witness2Email: Yup.string().trim().email('E-mail inválido').required('E-mail da testemunha 2 é obrigatório'),
    witness2Document: Yup.string().trim().required('Documento da testemunha 2 é obrigatório'),
});

function getSignatoriesFromContract(contract) {
    const parties = contract?.contractData?.signatories || {};
    return {
        contractedName: parties.contracted?.name || DEFAULT_SIGNATORIES.contractedName,
        contractedEmail: parties.contracted?.email || DEFAULT_SIGNATORIES.contractedEmail,
        contractedDocument: parties.contracted?.document || DEFAULT_SIGNATORIES.contractedDocument,
        witness1Name: parties.witness1?.name || DEFAULT_SIGNATORIES.witness1Name,
        witness1Email: parties.witness1?.email || DEFAULT_SIGNATORIES.witness1Email,
        witness1Document: parties.witness1?.document || DEFAULT_SIGNATORIES.witness1Document,
        witness2Name: parties.witness2?.name || DEFAULT_SIGNATORIES.witness2Name,
        witness2Email: parties.witness2?.email || DEFAULT_SIGNATORIES.witness2Email,
        witness2Document: parties.witness2?.document || DEFAULT_SIGNATORIES.witness2Document,
    };
}

function buildSignatoriesPayload(values) {
    return {
        contracted: {
            name: values.contractedName.trim(),
            email: values.contractedEmail.trim(),
            document: values.contractedDocument.trim(),
        },
        witness1: {
            name: values.witness1Name.trim(),
            email: values.witness1Email.trim(),
            document: values.witness1Document.trim(),
        },
        witness2: {
            name: values.witness2Name.trim(),
            email: values.witness2Email.trim(),
            document: values.witness2Document.trim(),
        },
    };
}

export function ContractTextPreview({ mutate, contract }) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const [viewMode, setViewMode] = useState('edit');
    const [isProcessing, setIsProcessing] = useState(false);

    const hasSavedText = Boolean(contract?.contractData?.contractText);
    const { data: defaultText, isFetching: loadingDefault } = useFetch(
        contract ? 'subscription/contract-text/default' : false,
    );

    const baseTemplate = useMemo(
        () => sanitizeContractHtmlForEditor(defaultText || ''),
        [defaultText],
    );

    const { values, setFieldValue, getFieldProps, isSubmitting, handleSubmit, errors, touched, setValues } = useFormik({
        initialValues: {
            contractText: '',
            ...DEFAULT_SIGNATORIES,
        },
        validationSchema: schema,
        onSubmit: async (formValues, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const current = formValues.contractText;

                await server.put(`subscription/${contract.uuid}/contract-text`, {
                    contractText: current,
                    ...(baseTemplate ? { contractTextSource: baseTemplate } : {}),
                    signatories: buildSignatoriesPayload(formValues),
                });
                await mutate?.();
                setSubmitting(false);
                enqueueSnackbar('Texto do contrato salvo.');
            } catch (error) {
                setSubmitting(false);
                setErrors(error);
            }
        },
    });

    useEffect(() => {
        if (!contract) return;

        const parties = getSignatoriesFromContract(contract);

        if (hasSavedText) {
            setValues({
                contractText: sanitizeContractHtmlForEditor(contract.contractData.contractText),
                ...parties,
            });
            return;
        }

        if (baseTemplate) {
            setValues({ contractText: baseTemplate, ...parties });
            return;
        }

        setValues(prev => ({ ...prev, ...parties }));
    }, [contract, baseTemplate, hasSavedText, setValues]);

    const previewContract = useMemo(
        () => ({
            ...contract,
            contractData: {
                ...contract?.contractData,
                signatories: buildSignatoriesPayload(values),
            },
        }),
        [contract, values],
    );

    // Visualizar usa o texto atual do formulário (após Processar/Salvar),
    // não o template base — senão o resultado do processamento é ignorado.
    const previewHtml = useMemo(
        () => applyContractPlaceholders(values.contractText || '', previewContract),
        [values.contractText, previewContract],
    );

    const pendingPlaceholders = useMemo(
        () => countPendingPlaceholders(values.contractText || '', previewContract),
        [values.contractText, previewContract],
    );

    const editPlaceholderCount = useMemo(
        () => countSimplePlaceholders(values.contractText),
        [values.contractText],
    );

    async function handleRestoreDefault() {
        try {
            const template = baseTemplate || (await fetchContractBaseTemplate());
            setValues(prev => ({
                ...prev,
                contractText: template,
                ...DEFAULT_SIGNATORIES,
            }));
            setViewMode('edit');
            enqueueSnackbar('Texto padrão restaurado. Processe e salve para aplicar na proposta.');
        } catch (error) {
            setErrors(error);
        }
    }

    async function handleProcessContent() {
        try {
            setIsProcessing(true);
            const template = baseTemplate || (await fetchContractBaseTemplate());

            if (!template || !hasContractPlaceholders(template)) {
                enqueueSnackbar('Template base sem variáveis {{...}} para processar.', {
                    variant: 'info',
                });
                return;
            }

            const withParties = {
                ...contract,
                contractData: {
                    ...contract?.contractData,
                    signatories: buildSignatoriesPayload(values),
                },
            };
            const processed = applyContractPlaceholders(template, withParties);

            setFieldValue('contractText', processed);
            setViewMode('preview');

            await server.put(`subscription/${contract.uuid}/contract-text`, {
                contractText: processed,
                contractTextSource: template,
                signatories: buildSignatoriesPayload(values),
            });
            await mutate?.();
            enqueueSnackbar('Conteúdo processado a partir do template padrão.');
        } catch (error) {
            setErrors(error);
        } finally {
            setIsProcessing(false);
        }
    }

    const isLoadingContent = !hasSavedText && loadingDefault && !values.contractText;
    const busy = isSubmitting || isProcessing || isLoadingContent;

    return (
        <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
                Use variáveis como <strong>{'{{RAZAO_SOCIAL}}'}</strong> no template padrão. Em{' '}
                <strong>Processar conteúdo</strong>, o texto é gerado sempre a partir do template base
                (não do HTML já salvo), preenchendo termo, plano, fidelidade, customização e adicionais.
                Ao salvar esses blocos, o contrato é reprocessado da mesma forma.
            </Typography>

            <Divider />

            <Typography variant="subtitle1" fontWeight={600}>
                Assinantes do documento
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Preencha nome, documento e e-mail do representante da contratada e das duas testemunhas. Esses
                dados entram no texto do contrato e serão enviados ao ZapSign na geração da assinatura.
            </Typography>

            <Stack spacing={1.5}>
                <Typography variant="subtitle2">Representante da contratada</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        label="Nome"
                        fullWidth
                        {...getFieldProps('contractedName')}
                        error={touched.contractedName && Boolean(errors.contractedName)}
                        helperText={touched.contractedName && errors.contractedName}
                    />
                    <TextField
                        label="CPF"
                        fullWidth
                        {...getFieldProps('contractedDocument')}
                        InputProps={{
                            inputComponent: DocumentInputMasked,
                            inputProps: { documentType: 'cpf' },
                        }}
                        error={touched.contractedDocument && Boolean(errors.contractedDocument)}
                        helperText={touched.contractedDocument && errors.contractedDocument}
                    />
                    <TextField
                        label="E-mail"
                        type="email"
                        fullWidth
                        {...getFieldProps('contractedEmail')}
                        error={touched.contractedEmail && Boolean(errors.contractedEmail)}
                        helperText={touched.contractedEmail && errors.contractedEmail}
                    />
                </Stack>

                <Typography variant="subtitle2">Testemunha 1</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        label="Nome"
                        fullWidth
                        {...getFieldProps('witness1Name')}
                        error={touched.witness1Name && Boolean(errors.witness1Name)}
                        helperText={touched.witness1Name && errors.witness1Name}
                    />
                    <TextField
                        label="CPF"
                        fullWidth
                        {...getFieldProps('witness1Document')}
                        InputProps={{
                            inputComponent: DocumentInputMasked,
                            inputProps: { documentType: 'cpf' },
                        }}
                        error={touched.witness1Document && Boolean(errors.witness1Document)}
                        helperText={touched.witness1Document && errors.witness1Document}
                    />
                    <TextField
                        label="E-mail"
                        type="email"
                        fullWidth
                        {...getFieldProps('witness1Email')}
                        error={touched.witness1Email && Boolean(errors.witness1Email)}
                        helperText={touched.witness1Email && errors.witness1Email}
                    />
                </Stack>

                <Typography variant="subtitle2">Testemunha 2</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        label="Nome"
                        fullWidth
                        {...getFieldProps('witness2Name')}
                        error={touched.witness2Name && Boolean(errors.witness2Name)}
                        helperText={touched.witness2Name && errors.witness2Name}
                    />
                    <TextField
                        label="CPF"
                        fullWidth
                        {...getFieldProps('witness2Document')}
                        InputProps={{
                            inputComponent: DocumentInputMasked,
                            inputProps: { documentType: 'cpf' },
                        }}
                        error={touched.witness2Document && Boolean(errors.witness2Document)}
                        helperText={touched.witness2Document && errors.witness2Document}
                    />
                    <TextField
                        label="E-mail"
                        type="email"
                        fullWidth
                        {...getFieldProps('witness2Email')}
                        error={touched.witness2Email && Boolean(errors.witness2Email)}
                        helperText={touched.witness2Email && errors.witness2Email}
                    />
                </Stack>
            </Stack>

            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={viewMode}
                    onChange={(_, nextMode) => nextMode && setViewMode(nextMode)}
                >
                    <ToggleButton value="edit">Editar</ToggleButton>
                    <ToggleButton value="preview">Visualizar</ToggleButton>
                </ToggleButtonGroup>

                {viewMode === 'preview' && pendingPlaceholders > 0 && (
                    <Chip
                        size="small"
                        color="error"
                        label={`${pendingPlaceholders} variável(is) ainda sem dados preenchidos`}
                    />
                )}
                {viewMode === 'edit' && editPlaceholderCount > 0 && (
                    <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        label={`${editPlaceholderCount} variável(is) {{…}} no texto`}
                    />
                )}
            </Stack>

            {isLoadingContent ? (
                <Stack alignItems="center" py={4}>
                    <CircularProgress size={28} />
                </Stack>
            ) : viewMode === 'edit' ? (
                <RichTextEditor
                    value={values.contractText}
                    onChange={content => setFieldValue('contractText', content)}
                    placeholder="Digite o texto do contrato..."
                    minHeight={360}
                    highlightPlaceholders
                />
            ) : (
                <RichTextEditor value={previewHtml} readOnly minHeight={360} highlightPlaceholders />
            )}

            {touched.contractText && errors.contractText && (
                <Typography variant="caption" color="error">
                    {errors.contractText}
                </Typography>
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={1} flexWrap="wrap">
                <Button variant="outlined" onClick={handleRestoreDefault} disabled={busy}>
                    Restaurar padrão
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleProcessContent}
                    disabled={busy || (!baseTemplate && !values.contractText)}
                    loading={isProcessing}
                >
                    Processar conteúdo
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={busy}
                    loading={isSubmitting}
                >
                    Salvar texto
                </Button>
            </Stack>
        </Stack>
    );
}
